import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Grid,
  Skeleton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Close from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import AdminHeader from "../../AdminHeader";
import AddOrganizationModel from "../models/AddOrganizationModel";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import { APP_API_V1_BASE } from "../../../config/api";
const API_URL = `${APP_API_V1_BASE}/organizations`;
const PAGE_BG = "#f6f8fb";
const PANEL_BG = "#ffffff";
const TEXT_PRIMARY = "#111827";
const TEXT_MUTED = "#667085";
const ACCENT = "#0f172a";
const ACCENT_SOFT = "#eef2ff";

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusTone = (status) =>
  status === "active"
    ? { color: "#166534", bg: "#dcfce7", border: "#86efac", label: "Active" }
    : { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", label: "Disabled" };

const typeTone = (type) => {
  const value = String(type || "").toUpperCase();
  const map = {
    OEM: { color: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" },
    DISTRIBUTOR: { color: "#9a3412", bg: "#ffedd5", border: "#fdba74" },
    CUSTOMER: { color: "#7c2d12", bg: "#ffedd5", border: "#fed7aa" },
    ENTERPRISE: { color: "#6d28d9", bg: "#f3e8ff", border: "#d8b4fe" },
  };
  return map[value] || { color: "#334155", bg: "#f8fafc", border: "#cbd5e1" };
};

const SummaryCard = ({ icon, label, value, tone = "default" }) => {
  const tones = {
    default: {
      bg: PANEL_BG,
      border: "rgba(17, 24, 39, 0.08)",
      iconBg: "#f3f4f6",
      iconColor: "#374151",
    },
    blue: {
      bg: "#f8fafc",
      border: "rgba(15, 23, 42, 0.08)",
      iconBg: "#e0e7ff",
      iconColor: "#3730a3",
    },
    green: {
      bg: "#f9fafb",
      border: "rgba(15, 23, 42, 0.08)",
      iconBg: "#dcfce7",
      iconColor: "#166534",
    },
    amber: {
      bg: "#fffaf5",
      border: "rgba(15, 23, 42, 0.08)",
      iconBg: "#ffedd5",
      iconColor: "#9a3412",
    },
  };
  const selected = tones[tone] || tones.default;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3.5,
        border: "1px solid",
        borderColor: selected.border,
        background: selected.bg,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        height: "100%",
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 18px 36px rgba(15,23,42,0.08)",
        },
      }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: selected.iconBg,
              color: selected.iconColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: TEXT_MUTED, fontWeight: 700 }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.03em",
                color: TEXT_PRIMARY,
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

function OrganizationsTableSkeleton() {
  const rows = Array.from({ length: 8 });
  const columns = Array.from({ length: 5 });

  return (
    <Box sx={{ height: 620, width: "100%", p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1.6fr 1fr",
            md: "1.6fr 1fr 1fr 1.1fr 1fr",
          },
          gap: 1.5,
          px: 1.5,
          py: 1.25,
          borderRadius: 2.5,
          backgroundColor: "#F8FAFC",
          border: "1px solid rgba(226,232,240,0.95)",
        }}
      >
        {columns.map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={index === 0 ? "62%" : "46%"}
            height={22}
            sx={{ display: { xs: index > 1 ? "none" : "block", md: "block" } }}
          />
        ))}
      </Box>

      <Stack spacing={1.1} sx={{ mt: 1.5 }}>
        {rows.map((_, index) => (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1.6fr 1fr",
                md: "1.6fr 1fr 1fr 1.1fr 1fr",
              },
              gap: 1.5,
              alignItems: "center",
              px: 1.5,
              py: 1.4,
              borderRadius: 2.5,
              border: "1px solid rgba(226,232,240,0.8)",
              backgroundColor: "#fff",
            }}
          >
            <Box>
              <Skeleton variant="text" width="64%" height={24} />
              <Skeleton variant="text" width="38%" height={18} />
            </Box>
            <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: 2 }} />
            <Skeleton
              variant="rounded"
              width={86}
              height={28}
              sx={{ borderRadius: 2, display: { xs: "none", md: "block" } }}
            />
            <Skeleton
              variant="text"
              width="72%"
              height={22}
              sx={{ display: { xs: "none", md: "block" } }}
            />
            <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "flex-end", md: "flex-start" } }}>
              <Skeleton variant="rounded" width={38} height={24} sx={{ borderRadius: 999 }} />
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function Organizations() {
  const { permissions, activeOrg } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [editOrg, setEditOrg] = useState(null);
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      const res = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
        },
      });
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  useEffect(() => {
    fetchOrganizations();
  }, [activeOrg]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openCreate") === "1") {
      setEditOrg(null);
      setOpenDialog(true);
    }
  }, [location.search]);

  const handleToggleStatus = async (orgId) => {
    if (!can(permissions, "organization.update")) return;
    const token = localStorage.getItem("authToken");
    const current = rows.find((r) => r.id === orgId);
    if (!current) return;
    const nextStatus = current.status === "active" ? "disabled" : "active";

    setRows((prev) =>
      prev.map((row) =>
        row.id === orgId ? { ...row, status: nextStatus } : row,
      ),
    );

    try {
      await axios.patch(
        `${API_URL}/${orgId}`,
        { status: nextStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
          },
        },
      );
    } catch (err) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === orgId ? { ...row, status: current.status } : row,
        ),
      );
      setError(err?.response?.data?.error || "Failed to update organization");
    }
  };

  const handleEdit = (row) => {
    if (!can(permissions, "organization.update")) return;
    setEditOrg(row);
    setOpenDialog(true);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const query = search.trim().toLowerCase();
      const matchesSearch =
        !query ||
        [row.name, row.code, row.type, row.status]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      const matchesStatus =
        statusFilter === "all" || row.status === statusFilter;
      const matchesType =
        typeFilter === "all" ||
        String(row.type || "").toLowerCase() === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [rows, search, statusFilter, typeFilter]);

  const stats = useMemo(() => {
    const active = rows.filter((row) => row.status === "active").length;
    const customer = rows.filter(
      (row) => String(row.type || "").toUpperCase() === "CUSTOMER",
    ).length;
    const partner = rows.filter((row) =>
      ["DISTRIBUTOR", "RESELLER"].includes(
        String(row.type || "").toUpperCase(),
      ),
    ).length;
    return {
      total: rows.length,
      active,
      disabled: rows.length - active,
      customer,
      partner,
    };
  }, [rows]);

  const columns = [
    {
      field: "name",
      headerName: "Organization",
      flex: 1.4,
      minWidth: 250,
      renderCell: ({ row }) => (
        <Stack spacing={0.35} justifyContent="center" sx={{ py: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: TEXT_PRIMARY,
              letterSpacing: "-0.01em",
            }}
          >
            {row.name}
          </Typography>
          <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
            {row.code || "No code assigned"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 150,
      renderCell: ({ value }) => {
        const tone = typeTone(value);
        return (
          <Chip
            label={String(value || "Unknown").toUpperCase()}
            size="small"
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              color: tone.color,
              backgroundColor: tone.bg,
              border: `1px solid ${tone.border}`,
              textTransform: "capitalize",
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Lifecycle",
      minWidth: 140,
      renderCell: ({ value }) => {
        const tone = statusTone(value);
        return (
          <Chip
            label={tone.label}
            size="small"
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              color: tone.color,
              backgroundColor: tone.bg,
              border: `1px solid ${tone.border}`,
              textTransform: "capitalize",
            }}
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created",
      minWidth: 190,
      flex: 0.8,
      valueFormatter: (params) => {
        const value =
          params && typeof params === "object" && "value" in params
            ? params.value
            : params;
        return formatDate(value);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          {can(permissions, "organization.update") && (
            <Tooltip title="Enable or disable organization">
              <Switch
                size="small"
                checked={row.status === "active"}
                onChange={() => handleToggleStatus(row.id)}
              />
            </Tooltip>
          )}
          {can(permissions, "organization.update") && (
            <Tooltip title="Edit organization">
              <IconButton size="small" onClick={() => handleEdit(row)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Open organization">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/administration/organizations/${row.id}`)
              }
            >
              <ArrowOutwardRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (!can(permissions, "organization.view")) {
    return <Navigate to="/403" replace />;
  }

  return (
    <>
      <Box
        sx={{
          background:
            "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
          p: { xs: 0, md: 1 },
        }}
      >
        <AdminHeader title="Organizations" />

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",

            mb: 4,
            mt: { xs: 2, md: 3 },
            mx: { xs: 1.5, md: 0.5 },
            px: { xs: 2, md: 3 },
            py: { xs: 3, md: 4 },
            borderRadius: 4,

            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

            boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
          }}
        >
          {/* Header */}
          <Box display="flex" gap={2} alignItems="flex-start">
            <Stack spacing={1.1}>
              <Typography
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.1,
                  letterSpacing: "-0.03em",
                  color: "#fff",

                  fontSize: {
                    xs: 28,
                    // sm: "2.2rem",
                    md: 40,
                  },
                }}
              >
                Organization Directory
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#fff", maxWidth: 680 , whiteSpace: { xs: "normal", md: "nowrap" } }}
              >
                Manage customer, partner, and OEM tenants with faster access to
                activation state and profile controls.
              </Typography>
            </Stack>
          </Box>

          {/* Buttons */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mt: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={fetchOrganizations}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#fff",
                color: "#fff",
              }}
            >
              Refresh
            </Button>

            {can(permissions, "organization.create") && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<AddRoundedIcon />}
                onClick={() => setOpenDialog(true)}
                sx={{
                  px: 2.2,
                  py: 1.1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  color: "#082F49",
                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                  boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                }}
              >
                Add Organization
              </Button>
            )}
          </Stack>

          {/*  Cards  */}
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr 1fr",
            }}
            gap={2}
            mt={2}
          >
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    p: 2,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",
                    color: "#fff",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Skeleton
                      variant="rounded"
                      width={52}
                      height={52}
                      sx={{ mr: 2, borderRadius: 2 }}
                    />
                    <Box flex={1}>
                      <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="40%" height={32} />
                    </Box>
                  </Box>
                </Card>
              ))
              : [
                {
                  title: "Total Organizations",
                  value: stats.total,
                  icon: <ApartmentRoundedIcon color="action" />,
                  bg: "#f1f5f9",
                },
                {
                  title: "Partner Accounts",
                  value: stats.partner,
                  icon: <StorefrontRoundedIcon sx={{ color: "#ac84f6ff" }} />,
                  bg: "#f5f3ff",
                },
                {
                  title: "Active Tenants",
                  value: stats.active,
                  icon: <VerifiedRoundedIcon sx={{ color: "#065f46" }} />,
                  bg: "#ecfdf5",
                },

                {
                  title: "Customer Accounts",
                  value: stats.customer,
                  icon: <StorefrontRoundedIcon sx={{ color: "#dc2626" }} />,
                  bg: "#fff5f5",
                },
              ].map((s, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    p: 2,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",
                    color: "#fff",
                    transition: "all 0.25s ease",

                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 15px 40px rgba(0,0,0,0.25)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    {/* ICON */}
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: s.bg,
                        mr: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {s.icon}
                    </Box>

                    {/* TEXT */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontWeight: 500,
                        }}
                      >
                        {s.title}
                      </Typography>

                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {Math.max(0, s.value).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 0, md: 0 } }}>
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },

                background:
                  "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "4",
              }}
            >
              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", xl: "center" }}
              >
                {/* LEFT */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: "-0.02em",
                      color: "#fff",
                      xs: 15,
                      md: 20
                    }}
                  >
                    Tenant Operations
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(203,213,225,0.75)" }}
                  >
                    Search and filter organizations by name, code, tenant type,
                    or lifecycle state.
                  </Typography>
                </Box>

                {/* RIGHT */}
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      minWidth: 140,
                      "& .MuiSelect-icon": {
                        color: "#fff",
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(203,213,225,0.7)",
                      },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(148,163,184,0.45)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(20,184,166,0.6)",
                        },
                      },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Type"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    sx={{
                      minWidth: 150,
                      "& .MuiSelect-icon": {
                        color: "#fff",
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(203,213,225,0.7)",
                      },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(148,163,184,0.45)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(20,184,166,0.6)",
                        },
                      },

                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="oem">OEM</MenuItem>
                    <MenuItem value="distributor">Distributor</MenuItem>
                    <MenuItem value="customer">Customer</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </TextField>

                  {(statusFilter !== "all" ||
                    typeFilter !== "all" ||
                    search) && (
                      <Button
                        size="small"
                        onClick={() => {
                          setStatusFilter("all");
                          setTypeFilter("all");
                          setSearch("");
                          setPaginationModel((p) => ({ ...p, page: 0 }));
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: 12,
                          height: 38,
                          color: "#fff",
                          border: "1px solid rgba(255,255,255,0.4)",
                          borderRadius: 2,
                          "&:hover": {
                            borderColor: "#fff",
                            backgroundColor: "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  <TextField
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, code, type, status"
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", md: 300 },

                      "& .MuiOutlinedInput-root": {
                        color: "#e2f8f5",
                        borderRadius: 2,

                        "& fieldset": {
                          borderColor: "rgba(148,163,184,0.25)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(148,163,184,0.45)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(20,184,166,0.6)",
                        },
                      },

                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(148, 163, 184, 0.55)",
                        opacity: 1,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon
                            fontSize="small"
                            sx={{ color: "rgba(203,213,225,0.6)" }}
                          />
                        </InputAdornment>
                      ),

                      // ✅ FIXED CONDITION
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearch(""); // 👈 clear input
                              setPaginationModel((p) => ({ ...p, page: 0 }));
                            }}
                            sx={{
                              color: "rgba(203,213,225,0.6)",
                              p: 0.5,

                              "&:hover": {
                                color: "#e2f8f5",
                                background: "rgba(255,255,255,0.08)",
                              },
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Stack>
              </Stack>
            </Box>

            {loading ? (
              <OrganizationsTableSkeleton />
            ) : (
              <Box sx={{ height: 620, width: "100%" }}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  pageSizeOptions={[10, 20, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10, page: 0 },
                    },
                  }}
                  onRowClick={(params, event) => {
                    if (event.target.closest(".MuiIconButton-root")) return;
                    if (event.target.closest(".MuiSwitch-root")) return;
                    navigate(`/administration/organizations/${params.row.id}`);
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Stack
                        sx={{ height: "100%" }}
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          No organizations found
                        </Typography>
                        <Typography color="text.secondary">
                          Try clearing filters or create a new organization.
                        </Typography>
                      </Stack>
                    ),
                  }}
                  sx={{
                    border: 0,
                    "--DataGrid-overlayHeight": "280px",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#fbfcfd",
                      borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
                      fontSize: 13,
                      fontWeight: 800,
                      color: "#475467",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 800,
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(17, 24, 39, 0.06)",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                      transition:
                        "background-color 160ms ease, transform 160ms ease",
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                      },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "1px solid rgba(17, 24, 39, 0.08)",
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <AddOrganizationModel
        open={openDialog}
        mode={editOrg ? "edit" : "create"}
        initialData={editOrg}
        onClose={() => {
          setOpenDialog(false);
          setEditOrg(null);
        }}
        onSave={(updatedOrg) => {
          setRows((prev) =>
            editOrg
              ? prev.map((r) =>
                r.id === updatedOrg.id ? { ...r, ...updatedOrg } : r,
              )
              : [updatedOrg, ...prev],
          );
        }}
      />
    </>
  );
}

export default Organizations;
